import {
	DataSourceField,
	DateLevels,
	normalizeName,
	JaqlDataSourceForDto,
} from '@sisense/sdk-data';
import { HttpClient } from '@sisense/sdk-rest-client';
import { getDataSourceSchema } from '@/services/data-models/data-models-service';
import { getHttpClient } from '@/singleton-clients';

export const DATA_SOURCES_API_URL_PATH = 'api/datasources';

/**
 * Data source schema table
 */
type DataSourceSchemaTable = {
	name: string;
	columns: {
		name: string;
		description: string | null;
	}[];
};

/**
 * Data source schema table
 */
type DataSourceSchemaDataset = {
	schema: {
		tables: DataSourceSchemaTable[];
	};
};

export type DataSourceFieldWithDescription = DataSourceField & {
	description?: string;
	tableDescription?: string;
};

export type NormalizedDataSourceField = {
	table: string;
	column: string;
	type: string;
	level?: string;
	expression: string;
	description: string;
};

/**
 * Add 'description' property from datasource schema columns to datasource fields
 */
function addDescriptionToFields(
	fields: DataSourceField[],
	datasets: DataSourceSchemaDataset[],
): DataSourceFieldWithDescription[] {
	const schemaDataDescriptionsMap = datasets.reduce(
		(map: Record<string, Record<string, string | null>>, dataset) => {
			dataset.schema.tables.forEach((table) => {
				map[table.name] = {};
				table.columns.forEach((column) => {
					map[table.name][column.name] = column.description;
				});
			});
			return map;
		},
		{},
	);

	return fields.map((field) => ({
		...field,
		description:
			schemaDataDescriptionsMap[field.table]?.[field.column] || '',
	})) as DataSourceFieldWithDescription[];
}

export type DataSourceStatus = 'ready' | 'draft';

export type DataSourceWithStatus = JaqlDataSourceForDto & {
	status?: DataSourceStatus;
	/**
	 * Elasticube datamodel UUID. Present for elasticubes (both ready and draft);
	 * undefined for live connections (which aren't elasticubes).
	 * Use this as `datamodelOid` when calling getModelSchema, addRelation,
	 * createPerspective, etc.
	 */
	oid?: string;
};

const ECM_GRAPHQL_URL = 'api/v2/ecm/';
const ELASTICUBES_METADATA_QUERY = `query elasticubesMetadata {
  elasticubesMetadata {
    oid
    title
    type
    status
    lastBuildTime
  }
}`;

interface ElasticubeMetadataItem {
	oid: string;
	title: string;
	type: string | null;
	status: string[] | string | null;
	lastBuildTime: string | null;
}

interface ElasticubesMetadataResponse {
	data?: {
		elasticubesMetadata?: ElasticubeMetadataItem[];
	};
}

async function fetchAllElasticubesMetadata(
	client: HttpClient,
): Promise<ElasticubeMetadataItem[]> {
	try {
		const response = await client.post<ElasticubesMetadataResponse>(
			ECM_GRAPHQL_URL,
			{
				operationName: 'elasticubesMetadata',
				query: ELASTICUBES_METADATA_QUERY,
			},
			{
				headers: {
					Accept: 'application/json, text/plain, */*',
					'Content-Type': 'application/json;charset=UTF-8',
					internal: 'true',
				},
			},
		);
		return response?.data?.elasticubesMetadata ?? [];
	} catch {
		// Older Sisense versions or restricted permissions: fall back silently
		return [];
	}
}

export async function getDataSources(
	httpClient?: HttpClient,
): Promise<DataSourceWithStatus[]> {
	const client = httpClient ?? getHttpClient();
	const url = `${DATA_SOURCES_API_URL_PATH}/?sharedWith=r,w`;
	const [builtAndLive, allElasticubes] = await Promise.all([
		client.get<JaqlDataSourceForDto[]>(url).then((r) => r ?? []),
		fetchAllElasticubesMetadata(client),
	]);

	const oidByTitle = new Map(
		(allElasticubes ?? []).map((m) => [m.title, m.oid]),
	);

	const ready: DataSourceWithStatus[] = builtAndLive.map((ds) => ({
		...ds,
		oid: oidByTitle.get(ds.title),
		status: 'ready' as const,
	}));
	const knownTitles = new Set(ready.map((ds) => ds.title));

	const drafts: DataSourceWithStatus[] = (allElasticubes ?? [])
		.filter((m) => !knownTitles.has(m.title))
		.map(
			(m) =>
				({
					title: m.title,
					id: m.oid,
					oid: m.oid,
					live: false,
					lastBuildTime: m.lastBuildTime ?? undefined,
					status: 'draft' as const,
				} as DataSourceWithStatus),
		);

	return [...ready, ...drafts];
}

/**
 * Get a data source by title
 */
export async function getDataSourceByTitle(
	dataSourceTitle: string,
	httpClient?: HttpClient,
): Promise<JaqlDataSourceForDto | null> {
	const client = httpClient ?? getHttpClient();
	const result = await client.get<JaqlDataSourceForDto>(
		`${DATA_SOURCES_API_URL_PATH}/${encodeURIComponent(dataSourceTitle)}`,
	);
	return result ?? null;
}

/**
 * Get data source fields
 */
export async function getDataSourceFields(
	dataSourceTitle: string,
	httpClient?: HttpClient,
): Promise<DataSourceField[] | null> {
	const client = httpClient ?? getHttpClient();
	return client
		.post<DataSourceField[]>(
			`${DATA_SOURCES_API_URL_PATH}/${encodeURIComponent(
				dataSourceTitle,
			)}/fields/search`,
			{
				offset: 0,
				count: 9999,
			},
		)
		.then((result) => result ?? null);
}

/**
 * Get data source fields with descriptions from schema
 */
export async function getDataSourceFieldsWithDescriptions(
	dataSourceTitle: string,
	httpClient?: HttpClient,
): Promise<DataSourceFieldWithDescription[] | DataSourceField[]> {
	const fields = await getDataSourceFields(dataSourceTitle, httpClient);
	if (!fields) return [];

	try {
		const dataSourceSchema = await getDataSourceSchema(
			dataSourceTitle,
			httpClient,
		);
		return dataSourceSchema?.datasets
			? addDescriptionToFields(
					fields,
					dataSourceSchema.datasets as DataSourceSchemaDataset[],
			  )
			: fields;
	} catch (error) {
		// Normalize various error shapes (fetch, axios, custom)
		const err: any = error as any;
		const status = err?.status ?? err?.statusCode ?? err?.response?.status;

		// Not found: schema endpoint missing in some deployments or hidden by permissions.
		if ((status === 404 || status === '404') && fields.length) {
			return fields;
		}

		// Unauthorized / Forbidden: user lacks permission to read schema. Fall back to fields without descriptions.
		if (
			status === 401 ||
			status === '401' ||
			status === 403 ||
			status === '403'
		) {
			console.log(
				`Note: Field descriptions are omitted from the data model due to restricted role of your account`,
			);
			return fields;
		}

		// Unknown error shape — rethrow to surface real issues instead of masking them.
		throw error;
	}
}

/**
 * Normalize data source fields (pure function - easily testable)
 */
export function normalizeFields(
	fields: DataSourceFieldWithDescription[] | DataSourceField[],
): NormalizedDataSourceField[] {
	const normalizedFields: NormalizedDataSourceField[] = [];

	fields.forEach((field) => {
		const normalizedName = normalizeName(field.column);
		const baseField = {
			table: normalizeName(field.table),
			column: normalizedName,
			type: field.dimtype,
			expression: field.id,
			description: field.description || `${field.table}.${field.column}`,
		};

		normalizedFields.push(baseField);

		if (field.dimtype === 'datetime') {
			// Add fields for each date level
			DateLevels.all.forEach((level) => {
				normalizedFields.push({
					...baseField,
					column: `${normalizedName}.${level}`,
					type: 'datetime',
					level: level,
					expression: field.id,
					description:
						field.description ||
						`${field.table}.${field.column}.${level}`,
				});
			});
		}
	});

	return normalizedFields;
}

/**
 * Get normalized data source fields
 * @param dataSourceTitle - The title of the data source
 * @param getFieldsFn - Optional function to get fields (for testing)
 */
export async function getNormalizedDataSourceFields(
	dataSourceTitle: string,
	getFieldsFn?: (
		title: string,
		client?: HttpClient,
	) => Promise<DataSourceFieldWithDescription[] | DataSourceField[]>,
	httpClient?: HttpClient,
): Promise<NormalizedDataSourceField[]> {
	const fieldsFn = getFieldsFn ?? getDataSourceFieldsWithDescriptions;
	const fields = await fieldsFn(dataSourceTitle, httpClient);
	return normalizeFields(fields);
}
